<template>
  <div class="cal-year">
    <button
      v-for="monthStart in months"
      :key="monthStart"
      type="button"
      class="cal-year-month"
      @click="selectMonth(monthStart)"
    >
      <div class="cal-year-month-title">{{ monthTitle(monthStart) }}</div>
      <div class="cal-year-mini-grid">
        <span
          v-for="cell in cellsFor(monthStart)"
          :key="cell.id"
          class="cal-year-day"
          :class="{
            'is-out': !cell.inMonth,
            'is-today': isToday(cell.id),
            'has-tasks': cell.inMonth && dayHasTasks(titlesFor(cell.id)),
          }"
        >
          <span class="cal-year-daynum">{{ dayNum(cell.id) }}</span>
          <span v-if="cell.inMonth && dayHasTasks(titlesFor(cell.id))" class="cal-year-dot" />
        </span>
      </div>
    </button>
  </div>
</template>

<script>
import moment from "moment";
const {
  yearMonthStarts,
  monthGridDays,
  dayHasTasks,
} = require("./calendarGrid.js");
const { isToday } = require("./dayNav.js");

export default {
  name: "CalendarYearView",
  props: {
    anchorDay: {
      type: String,
      required: true,
    },
    weekStartsOnMonday: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["select-month"],
  computed: {
    months() {
      if (!this.anchorDay) return [];
      return yearMonthStarts(this.anchorDay);
    },
  },
  methods: {
    isToday,
    dayHasTasks,
    dayNum(id) {
      return moment(String(id), "YYYYMMDD").date();
    },
    monthTitle(monthStart) {
      const lang = this.$store.getters.config.language;
      const isZh = String(lang || "").toLowerCase().startsWith("zh");
      const m = moment(String(monthStart), "YYYYMMDD").locale(lang);
      return isZh ? m.format("M月") : m.format("MMM");
    },
    cellsFor(monthStart) {
      return monthGridDays(monthStart, this.weekStartsOnMonday);
    },
    titlesFor(id) {
      const lists = this.$store.getters.todoLists || {};
      const list = lists[id];
      return Array.isArray(list) ? list : [];
    },
    selectMonth(monthStart) {
      const today = moment().format("YYYYMMDD");
      const ym = String(monthStart).slice(0, 6);
      const anchor =
        today.slice(0, 6) === ym ? today : String(monthStart);
      this.$emit("select-month", anchor);
    },
  },
};
</script>

<style scoped>
.cal-year {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 10px;
  padding: 4px 2px 16px;
}

.cal-year-month {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 6px;
  border: none;
  border-radius: 10px;
  background: var(--m-surface, #fff);
  color: inherit;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.04);
}

.cal-year-month-title {
  font-size: 13px;
  font-weight: 700;
  padding: 0 2px;
}

.cal-year-mini-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px 0;
}

.cal-year-day {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 18px;
  font-size: 9px;
  line-height: 1.1;
  color: inherit;
}

.cal-year-day.is-out {
  visibility: hidden;
}

.cal-year-day.is-today .cal-year-daynum {
  color: #0d6efd;
  font-weight: 700;
}

.cal-year-dot {
  width: 4px;
  height: 4px;
  margin-top: 1px;
  border-radius: 50%;
  background: #0d6efd;
}

.dark-theme .cal-year-month {
  background: var(--m-surface, #1c1c1e);
}

.dark-theme .cal-year-day.is-today .cal-year-daynum {
  color: #0a84ff;
}

.dark-theme .cal-year-dot {
  background: #0a84ff;
}

@media (min-width: 768px) {
  .cal-year {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px 14px;
    padding: 8px 4px 24px;
  }

  .cal-year-month {
    padding: 12px 10px;
  }

  .cal-year-month-title {
    font-size: 14px;
  }

  .cal-year-day {
    min-height: 22px;
    font-size: 10px;
  }
}
</style>
